import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Checkbox, TextField, InputAdornment, Chip, IconButton, Menu, CircularProgress, Divider, Tooltip } from '@mui/material';
import { FaSearch, FaPlus, FaEllipsisV, FaRegStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { projectsApi, organizationApi } from '../../../../../services/api';
import { EMPLOYEE_STATUSES, getStatusColor, getStatusTextColor } from '../../../../../constants/employeeStatuses';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import { EmployeeAvatar } from '../../../../common/EmployeeAvatar';
import { HeaderButton } from '../../../../common/styles';
import { FormContainer } from '../../styles';
import { EmployeesGrid, EmployeeCard, EmployeeCardContent, EmployeeName, EmployeeRole, EmployeeDetails } from './styles';
import { useAlert } from '../../../../../contexts/AlertContext';
import { useAbility } from '../../../../../contexts/AbilityContext';
import { useEmployeePermissions } from '../../../../../hooks/useEmployeePermissions';
import { resolveIndividualOpenMode } from '../../../../../utils/individualOpenMode';
import { INDIVIDUAL_PAGE_MODES } from '../../../../../constants/individualOpenModes';
import EmployeeTransfer from './components/EmployeeTransfer';
import RolesSelectionModal from '../../../../common/RolesSelectionModal';
import EmptyState from '../../../../common/EmptyState';
import { ROLE_TYPES } from '../../../../../constants/roleTypes';
import { PROJECT_PURPOSE_TYPES } from '../../../../../constants/projectPurposes';
import { EMPLOYEE_ENGAGEMENT_TYPES } from '../../../../../constants/employeeEngagementTypes';
import { BENCH_TYPES, getBenchTypeLabel, getBenchTypeColor } from '../../../../../constants/benchTypes';
import { ACTIONS, SUBJECTS } from '../../../../../constants/permissions';
import OverrideManagerModal from './components/OverrideManagerModal';
import EditEngagementModal from './components/EditEngagementModal';
import EndProjectModal from './components/EndProjectModal';
import EditBenchDataModal from './components/EditBenchDataModal';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import { formatDateOnly, fromPickerValue } from '../../../../../utils/dateOnly';

const statuses = Object.values(EMPLOYEE_STATUSES);

const Employees = ({ projectId, projectData, readOnly = false, mode }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const { canManageEmployee } = useEmployeePermissions();
  const isBenchProject = projectData?.projectPurpose?.code === PROJECT_PURPOSE_TYPES.BENCH;
  const canReadBenchData = ability.can(ACTIONS.READ, SUBJECTS.BENCH_EMPLOYEE_DATA);
  const canEditBenchData = ability.can(ACTIONS.UPDATE, SUBJECTS.BENCH_EMPLOYEE_DATA);
  const [employees, setEmployees] = useState([]);
  const [isEmployeesLoading, setIsEmployeesLoading] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState([EMPLOYEE_STATUSES.ENGAGED, EMPLOYEE_STATUSES.JOINING, EMPLOYEE_STATUSES.LEAVING]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showAttachEmployeeModal, setShowAttachEmployeeModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployeeForMenu, setSelectedEmployeeForMenu] = useState(null);
  const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [showEditEngagementModal, setShowEditEngagementModal] = useState(false);
  const [showEndProjectModal, setShowEndProjectModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showOverrideManagerModal, setShowOverrideManagerModal] = useState(false);
  const [futureAssignmentCancellationAvailability, setFutureAssignmentCancellationAvailability] = useState({});
  const [showCancelBenchModal, setShowCancelBenchModal] = useState(false);
  const [employeeForBenchCancellation, setEmployeeForBenchCancellation] = useState(null);
  const [leaveCancellationAvailability, setLeaveCancellationAvailability] = useState({});
  const [showCancelLeaveModal, setShowCancelLeaveModal] = useState(false);
  const [employeeForLeaveCancellation, setEmployeeForLeaveCancellation] = useState(null);
  const [showEditBenchDataModal, setShowEditBenchDataModal] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [projectId]);

  const fetchEmployees = async () => {
    try {
      setIsEmployeesLoading(true);
      const employeesData = await projectsApi.getProjectEmployees(projectId);
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    } finally {
      setIsEmployeesLoading(false);
    }
  };

  const handleMenuClick = async (event, employee) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedEmployeeForMenu(employee);

      // Check future assignment cancellation availability if this is not a default internal project
      if (projectData?.projectPurpose?.code !== PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL) {
        await checkFutureAssignmentCancellationAvailability(employee.id);
      }

      // Check leave cancellation availability
      await checkLeaveCancellationAvailability(employee.id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployeeForMenu(null);
  };

  const handleEditRoles = () => {
    setSelectedEmployee(selectedEmployeeForMenu);
    setSelectedPositionForRoles(selectedEmployeeForMenu.position);
    
    const functionalRoleIds = selectedEmployeeForMenu.roles
      ?.filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
      .map(role => role.id) || [];
      
    const technicalRoleIds = selectedEmployeeForMenu.roles
      ?.filter(role => role.type === ROLE_TYPES.TECHNICAL)
      .map(role => role.id) || [];
    
    setSelectedFunctionalRoles(functionalRoleIds);
    setSelectedTechnicalRoles(technicalRoleIds);
    setShowEditRolesModal(true);
    handleMenuClose();
  };

  const handleEditRolesSubmit = async () => {
    try {
      await projectsApi.updateEmployeeProjectRoles(selectedEmployee.employeeProjectId, {
        roleIds: [...selectedFunctionalRoles, ...selectedTechnicalRoles]
      });
      
      await fetchEmployees();
      showAlert('Project roles updated successfully', 'success');
      setShowEditRolesModal(false);
      setSelectedEmployee(null);
      setSelectedPositionForRoles(null);
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
    } catch (error) {
      console.error('Error updating project roles:', error);
      showAlert(error.message || 'Error updating project roles', 'error', error);
    }
  };

  const handleEditEngagementClick = () => {
    setSelectedEmployee(selectedEmployeeForMenu);
    setShowEditEngagementModal(true);
    handleMenuClose();
  };

  const handleEditEngagementSuccess = async () => {
    await fetchEmployees();
    setShowEditEngagementModal(false);
    setSelectedEmployee(null);
  };

  const handleEndProject = () => {
    setSelectedEmployee(selectedEmployeeForMenu);
    setShowEndProjectModal(true);
    handleMenuClose();
  };

  const handleEndProjectSuccess = async () => {
    await fetchEmployees();
    setShowEndProjectModal(false);
    setSelectedEmployee(null);
  };

  const handleAttachEmployee = async (data) => {
    try {
      // Check if this is a DEFAULT_INTERNAL attach operation
      if (data.isDefaultInternal) {
        // DEFAULT_INTERNAL flow
        await organizationApi.attachEmployeeToDefaultInternal(
          data.businessUnitId,
          data.employeeId,
          data.managerId
        );
        showAlert('Individual attached to default internal project successfully', 'success');
      } else if (data.source?.employeeId) {
        // Transfer flow
        const transferPayload = {
          source: {
            projectId: data.source.projectId,
            employeeId: data.source.employeeId,
            endDate: fromPickerValue(data.source.endDate)
          },
          destination: {
            projectId: data.destination.projectId,
            employeeId: data.destination.employeeId,
            engagementTypeId: data.destination.engagementTypeId,
            startDate: fromPickerValue(data.destination.startDate),
            teamId: data.destination.teamId,
            roleIds: data.destination.roleIds
          }
        };

        // Include bench data when transferring to a bench project
        if (data.destination.benchType) {
          transferPayload.destination.benchType = data.destination.benchType;
          transferPayload.destination.flagAsStar = data.destination.flagAsStar || false;
          transferPayload.destination.comments = data.destination.comments || null;
          transferPayload.destination.benchManagerId = data.destination.benchManagerId || null;
          transferPayload.destination.salary = data.destination.salary || null;
          transferPayload.destination.salaryCurrency = data.destination.salaryCurrency || null;
          transferPayload.destination.salaryPeriod = data.destination.salaryPeriod || null;
          transferPayload.destination.salaryAmountType = data.destination.salaryAmountType || null;
        }

        await projectsApi.transferEmployee(transferPayload);
        showAlert('Individual transferred successfully', 'success');
      } else {
        // Regular attach flow
        const attachPayload = {
          employeeId: data.destination.employeeId,
          engagementTypeId: data.destination.engagementTypeId,
          startDate: fromPickerValue(data.destination.startDate),
          teamId: data.destination.teamId,
          roleIds: data.destination.roleIds
        };

        // Include bench data when attaching to a bench project
        if (data.destination.benchType) {
          attachPayload.benchType = data.destination.benchType;
          attachPayload.flagAsStar = data.destination.flagAsStar || false;
          attachPayload.comments = data.destination.comments || null;
          attachPayload.benchManagerId = data.destination.benchManagerId || null;
          attachPayload.salary = data.destination.salary || null;
          attachPayload.salaryCurrency = data.destination.salaryCurrency || null;
          attachPayload.salaryPeriod = data.destination.salaryPeriod || null;
          attachPayload.salaryAmountType = data.destination.salaryAmountType || null;
        }

        await projectsApi.attachEmployee(projectId, attachPayload);
        showAlert('Individual attached successfully', 'success');
      }
      
      await fetchEmployees();
      setShowAttachEmployeeModal(false);
      setShowTransferModal(false);
    } catch (error) {
      console.error('Error handling employee operation:', error);
      showAlert(error.message || 'Error processing individual operation', 'error', error);
    }
  };

  const handleTransfer = () => {
    setSelectedEmployee(selectedEmployeeForMenu);
    setShowTransferModal(true);
    handleMenuClose();
  };

  const handleOverrideManager = () => {
    setSelectedEmployee(selectedEmployeeForMenu);
    setShowOverrideManagerModal(true);
    handleMenuClose();
  };

  const handleOverrideManagerSuccess = async () => {
    await fetchEmployees();
    setShowOverrideManagerModal(false);
    setSelectedEmployee(null);
  };

  const handleEditBenchData = () => {
    setSelectedEmployee(selectedEmployeeForMenu);
    setShowEditBenchDataModal(true);
    handleMenuClose();
  };

  const handleEditBenchDataSuccess = async () => {
    await fetchEmployees();
    setShowEditBenchDataModal(false);
    setSelectedEmployee(null);
  };

  const handleEditEmployee = () => {
    if (selectedEmployeeForMenu) {
      // ADR-109 §1 (was ADR-017's inline rule): one resolver for the Project page, the
      // Individuals list and the reports — resigned → view, `leaving` stays editable.
      const mode = resolveIndividualOpenMode({ employee: selectedEmployeeForMenu, canManageEmployee });
      navigate(`/employees/${selectedEmployeeForMenu.id}/${mode}`);
    }
    handleMenuClose();
  };

  const checkFutureAssignmentCancellationAvailability = async (employeeId) => {
    try {
      const result = await projectsApi.canCancelFutureAssignment(employeeId, projectId);
      setFutureAssignmentCancellationAvailability(prev => ({
        ...prev,
        [employeeId]: result.canCancel
      }));
    } catch (error) {
      setFutureAssignmentCancellationAvailability(prev => ({
        ...prev,
        [employeeId]: false
      }));
    }
  };

  const checkLeaveCancellationAvailability = async (employeeId) => {
    try {
      const result = await projectsApi.canCancelProjectLeave(employeeId, projectId);
      setLeaveCancellationAvailability(prev => ({
        ...prev,
        [employeeId]: result.canCancel
      }));
    } catch (error) {
      setLeaveCancellationAvailability(prev => ({
        ...prev,
        [employeeId]: false
      }));
    }
  };

  const handleCancelBenchAssignment = () => {
    setEmployeeForBenchCancellation(selectedEmployeeForMenu);
    setShowCancelBenchModal(true);
    handleMenuClose();
  };

  const confirmCancelBenchAssignment = async () => {

    if (!employeeForBenchCancellation) {
      return;
    }

    try {
      await projectsApi.cancelFutureAssignment(employeeForBenchCancellation.id, projectId);
      await fetchEmployees();
      showAlert('Project allocation cancelled successfully', 'success');
      setShowCancelBenchModal(false);
      setEmployeeForBenchCancellation(null);
    } catch (error) {
      showAlert('Failed to cancel project allocation: ' + error.message, 'error');
      setShowCancelBenchModal(false);
    }
  };

  const handleCancelProjectLeave = () => {
    setEmployeeForLeaveCancellation(selectedEmployeeForMenu);
    setShowCancelLeaveModal(true);
    handleMenuClose();
  };

  const confirmCancelProjectLeave = async () => {

    if (!employeeForLeaveCancellation) {
      return;
    }

    try {
      await projectsApi.cancelProjectLeave(employeeForLeaveCancellation.id, projectId);
      await fetchEmployees();
      showAlert('Project leave cancelled successfully', 'success');
      setShowCancelLeaveModal(false);
      setEmployeeForLeaveCancellation(null);
    } catch (error) {
      showAlert('Failed to cancel project leave: ' + error.message, 'error');
      setShowCancelLeaveModal(false);
    }
  };

  if (isEmployeesLoading) {
    return <LoadingSpinner />;
  }

  // Filter employees based on selected statuses and search term
  const filteredEmployees = (employees || []).filter(emp => {
    const matchesStatus = selectedStatuses.includes(emp.status);
    const matchesSearch = !employeeSearchTerm || 
      emp.fullName?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      emp.position?.label?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      emp.grade?.label?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      emp.roles?.some(role => role.label?.toLowerCase().includes(employeeSearchTerm.toLowerCase()));
    
    return matchesStatus && matchesSearch;
  });

  return (
    <FormContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ width: '400px' }}>
            <InputLabel sx={{ 
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)'
              },
              transform: 'translate(14px, 8px)'
            }}>Status</InputLabel>
            <Select
              multiple
              value={selectedStatuses}
              label="Status"
              onChange={(e) => setSelectedStatuses(e.target.value)}
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value.charAt(0).toUpperCase() + value.slice(1)}
                      size="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onDelete={(e) => {
                        e.stopPropagation();
                        setSelectedStatuses(prev => prev.filter(s => s !== value));
                      }}
                      sx={{
                        margin: '2px',
                        backgroundColor: getStatusColor(value),
                        color: getStatusTextColor(value),
                        borderColor: getStatusTextColor(value),
                        '& .MuiChip-deleteIcon': {
                          color: `${getStatusTextColor(value)} !important`
                        }
                      }}
                    />
                  ))}
                </div>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200
                  }
                }
              }}
              sx={{
                height: '40px',
                fontSize: '14px',
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  minHeight: '40px !important',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {statuses.map((status) => (
                <MenuItem 
                  key={status} 
                  value={status}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    transition: 'background-color 0.2s ease',
                    '& > div': {
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(status),
                      color: getStatusTextColor(status),
                      width: 'fit-content',
                      border: '1px solid transparent',
                      transition: 'border-color 0.2s ease'
                    },
                    '&.Mui-selected > div': {
                      borderColor: getStatusTextColor(status)
                    },
                    '&:hover > div': {
                      borderColor: getStatusTextColor(status)
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'transparent'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
                    }
                  }}
                >
                  <div>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            placeholder="Search individual..."
            value={employeeSearchTerm}
            onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '300px',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px'
              }
            }}
          />
        </Box>

        {!readOnly && (
          <HeaderButton onClick={() => setShowAttachEmployeeModal(true)}>
            <FaPlus /> Attach Individual
          </HeaderButton>
        )}
      </Box>
      
      <div style={{ 
        color: '#64748B', 
        fontSize: '14px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <span style={{ fontWeight: 600 }}>
          {filteredEmployees.length}
        </span>
        {filteredEmployees.length === 1 ? 'individual' : 'individuals'} found
      </div>

      {filteredEmployees.length === 0 ? (
        <EmptyState
          title="No Individuals Found"
          description="No individuals found matching the current filters. Try adjusting your search criteria or filters."
        />
      ) : (
        <EmployeesGrid>
          {filteredEmployees.map((employee) => (
            <EmployeeCard key={employee.employeeProjectId}>
              <EmployeeCardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <EmployeeAvatar employee={employee} size={48} />
                      <div>
                        <EmployeeName>{employee.fullName}</EmployeeName>
                        <EmployeeRole>
                          {employee.position?.label || 'No position assigned'}
                          {employee.grade?.label && ` • ${employee.grade.label}`}
                        </EmployeeRole>
                        {employee.timeInProject && (
                          <div style={{
                            marginTop: '4px',
                            color: '#64748B',
                            fontSize: '13px',
                            fontStyle: 'italic'
                          }}>
                            {employee.timeInProject.years === 0 &&
                             employee.timeInProject.months === 0 &&
                             employee.timeInProject.days === 0
                              ? 'Not started yet'
                              : (
                                <>
                                  {employee.timeInProject.years > 0 && `${employee.timeInProject.years} year${employee.timeInProject.years !== 1 ? 's' : ''}`}
                                  {employee.timeInProject.years > 0 && employee.timeInProject.months > 0 && ', '}
                                  {employee.timeInProject.months > 0 && `${employee.timeInProject.months} month${employee.timeInProject.months !== 1 ? 's' : ''}`}
                                  {((employee.timeInProject.years > 0 || employee.timeInProject.months > 0) && employee.timeInProject.days > 0) && ', '}
                                  {employee.timeInProject.days > 0 && `${employee.timeInProject.days} day${employee.timeInProject.days !== 1 ? 's' : ''}`}
                                  {' on the Project'}
                                </>
                              )}
                          </div>
                        )}
                        {isBenchProject && canReadBenchData && employee.benchData && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                            {employee.benchData.benchType !== BENCH_TYPES.BENCH && (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: getBenchTypeColor(employee.benchData.benchType) + '1A',
                                color: getBenchTypeColor(employee.benchData.benchType),
                              }}>
                                {getBenchTypeLabel(employee.benchData.benchType)}
                              </span>
                            )}
                            {employee.benchData.flagAsStar && (
                              <Tooltip title="Top talent">
                                <span style={{ color: '#E65100', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                                  <FaRegStar />
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        backgroundColor: getStatusColor(employee.status),
                        color: getStatusTextColor(employee.status),
                      }}>
                        {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                      </div>
                      {!readOnly && (
                        (projectData?.projectPurpose?.code !== PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL) ||
                        canManageEmployee(employee)
                      ) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, employee)}
                          sx={{ color: '#64748B' }}
                        >
                          <FaEllipsisV />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </div>
                <EmployeeDetails>
                  <div><strong>Email:</strong> {employee.email}</div>
                  <div><strong>Roles:</strong> {employee.roles?.map(role => role.label).join(', ') || 'No roles assigned'}</div>
                  <div><strong>Engagement:</strong> {employee.engagementType?.name || 'No engagement attached'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>Represented by:</strong>
                    {employee.directManager ? (
                      <>
                        <EmployeeAvatar employee={employee.directManager} size={24} />
                        <span>{employee.directManager.fullName}</span>
                      </>
                    ) : (
                      <span>Not assigned</span>
                    )}
                  </div>
                  <div>
                    <strong>Start Date on Project:</strong> {employee.projectStartDate ? formatDateOnly(employee.projectStartDate, 'MMM D, YYYY') : 'Not set'}
                  </div>
                  {employee.projectEndDate && (
                    <div>
                      <strong>End Date on Project:</strong> {formatDateOnly(employee.projectEndDate, 'MMM D, YYYY')}
                    </div>
                  )}
                </EmployeeDetails>
              </EmployeeCardContent>
            </EmployeeCard>
          ))}
        </EmployeesGrid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canManageEmployee(selectedEmployeeForMenu) && (
          <>
            <MenuItem onClick={handleEditEmployee}>
              {/* ADR-109 §1: label follows the same resolver as handleEditEmployee */}
              {resolveIndividualOpenMode({ employee: selectedEmployeeForMenu, canManageEmployee }) === INDIVIDUAL_PAGE_MODES.EDIT
                ? 'Edit Individual'
                : 'View Individual'}
            </MenuItem>
            <Divider />
          </>
        )}
        {[EMPLOYEE_STATUSES.ENGAGED, EMPLOYEE_STATUSES.JOINING, EMPLOYEE_STATUSES.LEAVING].includes(selectedEmployeeForMenu?.status) && (
          <>
            <MenuItem onClick={handleEditRoles}>
              Assign Project Roles
            </MenuItem>
            {selectedEmployeeForMenu?.engagementType?.code !== EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME && (
              <MenuItem
                onClick={handleEditEngagementClick}
              >
                Update Engagement
              </MenuItem>
            )}
            {selectedEmployeeForMenu?.status === EMPLOYEE_STATUSES.ENGAGED && (
              <MenuItem
                onClick={handleEndProject}
                disabled={selectedEmployeeForMenu?.engagementType?.code === EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME}
              >
                End Project Allocation
              </MenuItem>
            )}
            {selectedEmployeeForMenu?.status === EMPLOYEE_STATUSES.ENGAGED && (
              <MenuItem
                onClick={handleTransfer}
                disabled={selectedEmployeeForMenu?.engagementType?.code !== EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME}
              >
                Transfer Individual
              </MenuItem>
            )}
            {(projectData?.projectPurpose?.code === PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL ||
              projectData?.projectPurpose?.code === PROJECT_PURPOSE_TYPES.BENCH) &&
              selectedEmployeeForMenu?.engagementType?.code === EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME && (
              <MenuItem onClick={handleOverrideManager}>
                Override Representative
              </MenuItem>
            )}
          </>
        )}
        {isBenchProject && canEditBenchData && (
          <MenuItem onClick={handleEditBenchData}>
            Edit Bench Data
          </MenuItem>
        )}
         {projectData?.projectPurpose?.code !== PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL && (
          <MenuItem
            onClick={handleCancelBenchAssignment}
            disabled={!futureAssignmentCancellationAvailability[selectedEmployeeForMenu?.id]}
            sx={{
              color: futureAssignmentCancellationAvailability[selectedEmployeeForMenu?.id] ? 'warning.main' : 'text.disabled',
              '&:hover': futureAssignmentCancellationAvailability[selectedEmployeeForMenu?.id] ? {
                color: 'warning.dark'
              } : {},
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            Cancel Project Allocation
            {futureAssignmentCancellationAvailability[selectedEmployeeForMenu?.id] === undefined && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </MenuItem>
        )}
        <MenuItem
          onClick={handleCancelProjectLeave}
          disabled={!leaveCancellationAvailability[selectedEmployeeForMenu?.id]}
          sx={{
            color: leaveCancellationAvailability[selectedEmployeeForMenu?.id] ? 'warning.main' : 'text.disabled',
            '&:hover': leaveCancellationAvailability[selectedEmployeeForMenu?.id] ? {
              color: 'warning.dark'
            } : {},
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          Cancel Project Leave
          {leaveCancellationAvailability[selectedEmployeeForMenu?.id] === undefined && (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          )}
        </MenuItem>
      </Menu>

      <EmployeeTransfer
        isOpen={showAttachEmployeeModal || showTransferModal}
        onClose={() => {
          if (showTransferModal) {
            setShowTransferModal(false);
          } else {
            setShowAttachEmployeeModal(false);
          }
          setSelectedEmployee(null);
        }}
        projectId={projectId}
        projectData={projectData}
        onSubmit={handleAttachEmployee}
        employeeId={showTransferModal ? selectedEmployee?.id : undefined}
        employees={employees}
      />

      {/* Edit roles modal */}
      <RolesSelectionModal
        isOpen={showEditRolesModal}
        onClose={() => {
          setShowEditRolesModal(false);
          setSelectedEmployee(null);
          setSelectedPositionForRoles(null);
          setSelectedFunctionalRoles([]);
          setSelectedTechnicalRoles([]);
        }}
        onSubmit={handleEditRolesSubmit}
        selectedPositionForRoles={selectedPositionForRoles}
        setSelectedPositionForRoles={setSelectedPositionForRoles}
        selectedFunctionalRoles={selectedFunctionalRoles}
        setSelectedFunctionalRoles={setSelectedFunctionalRoles}
        selectedTechnicalRoles={selectedTechnicalRoles}
        setSelectedTechnicalRoles={setSelectedTechnicalRoles}
        initialPosition={selectedEmployee?.position}
        lockPosition={true}
      />

      {/* Edit engagement modal */}
      <EditEngagementModal
        isOpen={showEditEngagementModal}
        onClose={() => {
          setShowEditEngagementModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onSuccess={handleEditEngagementSuccess}
      />

      {/* End project modal */}
      <EndProjectModal
        isOpen={showEndProjectModal}
        onClose={() => {
          setShowEndProjectModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onSuccess={handleEndProjectSuccess}
      />

      {/* Override Manager Modal */}
      <OverrideManagerModal
        isOpen={showOverrideManagerModal}
        onClose={() => {
          setShowOverrideManagerModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        projectId={projectId}
        businessUnitId={projectData?.businessUnit?.id}
        onSuccess={handleOverrideManagerSuccess}
      />

      {/* Cancel Project Allocation Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelBenchModal}
        onClose={() => {
          setShowCancelBenchModal(false);
          setEmployeeForBenchCancellation(null);
        }}
        onConfirm={() => {
          confirmCancelBenchAssignment();
        }}
        title="Cancel Project Allocation"
        message={`Are you sure you want to cancel the project allocation for ${employeeForBenchCancellation?.fullName}? This action will extend their current project indefinitely.`}
        actionButtonText="Cancel Allocation"
        actionButtonColor="#f59e0b"
      />

      {/* Cancel Project Leave Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelLeaveModal}
        onClose={() => {
          setShowCancelLeaveModal(false);
          setEmployeeForLeaveCancellation(null);
        }}
        onConfirm={() => {
          confirmCancelProjectLeave();
        }}
        title="Cancel Project Leave"
        message={`Are you sure you want to cancel the project leave for ${employeeForLeaveCancellation?.fullName}? This action will cancel their scheduled departure from the project.`}
        actionButtonText="Cancel Leave"
        actionButtonColor="#f59e0b"
      />

      {/* Edit Bench Data Modal */}
      {isBenchProject && (
        <EditBenchDataModal
          key={selectedEmployee?.employeeProjectId}
          isOpen={showEditBenchDataModal}
          onClose={() => {
            setShowEditBenchDataModal(false);
            setSelectedEmployee(null);
          }}
          employee={selectedEmployee}
          onSuccess={handleEditBenchDataSuccess}
        />
      )}
    </FormContainer>
  );
};

export default Employees;
